import React, { useState } from 'react';
import {
  Container, Typography, Box, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
  Grid, Paper, List, ListItem, ListItemIcon, IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import customTheme from '../../theme';

const GradientHero = styled(Box)({
  background: `linear-gradient(135deg, ${customTheme.colors.secondary[300]} 0%, ${customTheme.colors.secondary[500]} 100%)`,
  color: customTheme.colors.secondary[900],
  py: 6,
  borderRadius: customTheme.borderRadius.lg,
  mb: 4,
  textAlign: 'center',
  position: 'relative',
});

export default function Career() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const benefits = [
    t('career_page.benefits.remote'),
    t('career_page.benefits.team'),
    t('career_page.benefits.projects'),
    t('career_page.benefits.career'),
    t('career_page.benefits.package'),
    t('career_page.benefits.startup'),
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('career_page.form.name_required');
    if (!formData.email.trim()) {
      newErrors.email = t('career_page.form.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('career_page.form.email_invalid');
    }
    if (!formData.phone.trim()) newErrors.phone = t('career_page.form.phone_required');
    if (!formData.message.trim()) newErrors.message = t('career_page.form.motivation_required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await fetch('/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subject: `Candidature spontanee`,
        }),
      });
      if (response.ok) {
        setSnackbar({ open: true, message: t('career_page.form.success'), severity: 'success' });
        setApplyDialogOpen(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Error');
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('career_page.form.error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <GradientHero sx={{ p: { xs: 4, md: 6 } }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{
              position: 'absolute',
              left: { xs: 8, md: 16 },
              top: { xs: 8, md: 16 },
              color: 'inherit',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            {t('career_page.title')}
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.85 }}>
            {t('career_page.subtitle')}
          </Typography>
        </GradientHero>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 3, color: customTheme.colors.primary[800] }}>
              {t('career_page.why_join')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3 }}>
              {t('career_page.why_join_text_1')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
              {t('career_page.why_join_text_2')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', border: '1px solid', borderColor: customTheme.colors.secondary[300] }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: customTheme.colors.primary[800] }}>
                {t('career_page.benefits_title')}
              </Typography>
              <List dense>
                {benefits.map((benefit, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleIcon sx={{ color: customTheme.colors.primary[600], fontSize: 20 }} />
                    </ListItemIcon>
                    <Typography variant="body1">{benefit}</Typography>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, borderRadius: customTheme.borderRadius.lg, textAlign: 'center', backgroundColor: customTheme.colors.primary[600], color: '#fff' }}>
          <WorkIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            {t('career_page.spontaneous_title')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto', opacity: 0.9, fontSize: '1.1rem' }}>
            {t('career_page.spontaneous_text')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setApplyDialogOpen(true)}
            startIcon={<SendIcon />}
            sx={{
              px: 5,
              py: 2,
              borderRadius: 3,
              fontSize: '1.2rem',
              fontWeight: 600,
              backgroundColor: 'white',
              color: customTheme.colors.primary[800],
              '&:hover': {
                backgroundColor: 'grey.100',
              },
            }}
          >
            {t('career_page.spontaneous_cta')}
          </Button>
        </Paper>
      </Container>

      <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, pt: 3, color: customTheme.colors.primary[800] }}>
          {t('career_page.form.spontaneous_title')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2 }}>
          <Box component="form" noValidate>
            <TextField
              fullWidth
              label={t('career_page.form.name')}
              margin="normal"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
            <TextField
              fullWidth
              label={t('career_page.form.email')}
              margin="normal"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
            <TextField
              fullWidth
              label={t('career_page.form.phone')}
              margin="normal"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
            <TextField
              fullWidth
              label={t('career_page.form.motivation')}
              margin="normal"
              multiline
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              error={!!errors.message}
              helperText={errors.message}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setApplyDialogOpen(false)} color="inherit">
            {t('career_page.form.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={!loading && <SendIcon />}
          >
            {loading ? t('career_page.form.sending') : t('career_page.form.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
